import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import PettyCashPanel from '@/components/PettyCashPanel';

const TOLERANCE = 25; // rand — over this, closing needs a manager PIN

interface Shift {
  shiftId: string;
  floatOpening?: string;
  expectedCash?: string;
  cashSales?: string;
  pettyCash?: string;
  cardSales?: string;
  mobileSales?: string;
  accountSales?: string;
  saleCount?: number;
  openedAt?: string;
}

export default function CashUpPanel({ branchId, branchName }: { branchId: string; branchName?: string }) {
  const [shift, setShift] = useState<Shift | null>(null);
  const [loading, setLoading] = useState(true);
  const [counted, setCounted] = useState('');
  const [notes, setNotes] = useState('');
  const [floatAmount, setFloatAmount] = useState('500.00');
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [history, setHistory] = useState<any[]>([]);

  const load = async () => {
    if (!branchId) return;
    try {
      const res = await apiClient.getCurrentShift(branchId);
      setShift(res.data);
    } catch {
      setShift(null); // no open shift is a normal state, not an error
    }
    try {
      const h = await apiClient.getShiftHistory(branchId);
      setHistory(Array.isArray(h.data) ? h.data.slice(0, 5) : []);
    } catch {
      setHistory([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const expected = Number(shift?.expectedCash ?? 0);
  // The backend carries the float forward from the last close unless overridden
  const lastClosed = history.find((h: any) => h.countedCash != null);
  const carriedFloat = Number(lastClosed?.countedCash ?? 0);
  const lastClose = lastClosed?.closedAt;
  const variance = counted === '' ? null : Number(counted) - expected;
  const overTolerance = variance !== null && Math.abs(variance) > TOLERANCE;

  const openShift = async () => {
    if (override && (!overrideReason.trim() || !managerPin.trim())) {
      setErr('Changing the float needs both a reason and Mahlomola\'s PIN.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await apiClient.openShift(
        branchId,
        Number(floatAmount).toFixed(2),
        override ? overrideReason.trim() : undefined,
        override ? managerPin.trim() : undefined,
      );
      setMsg('Shift opened.');
      setOverride(false);
      setOverrideReason('');
      setManagerPin('');
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not open shift');
    } finally {
      setBusy(false);
    }
  };

  const closeShift = async () => {
    if (counted === '') {
      setErr('Enter the counted cash first.');
      return;
    }
    if (overTolerance && !notes.trim()) {
      setErr(`Variance is over R${TOLERANCE} — a reason is required before closing.`);
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await apiClient.closeShift(shift!.shiftId, Number(counted).toFixed(2), notes.trim() || undefined);
      setMsg('Shift closed and recorded.');
      setCounted('');
      setNotes('');
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not close shift');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading shift…</div>;

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      {!shift ? (
        <div className="bg-white rounded-lg shadow p-6 max-w-md">
          <h3 className="text-lg font-medium text-gray-900">No shift open</h3>
          <p className="text-sm text-gray-500 mt-1">
            Open the till for {branchName || 'this branch'}.
          </p>

          <div className="mt-5 rounded-lg bg-gray-50 border border-gray-200 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Float carried forward</p>
            <p className="text-2xl font-bold tabular-nums text-gray-900 mt-1">R {carriedFloat.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {lastClose
                ? `Counted at the last close on ${new Date(lastClose).toLocaleDateString('en-ZA', { dateStyle: 'medium' })}`
                : 'No previous shift to carry from'}
            </p>
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} className="rounded" />
            Start with a different amount
          </label>

          {override && (
            <div className="mt-3 space-y-3 border-l-2 border-amber-400 pl-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening float</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 font-medium">R</span>
                  <input
                    type="number"
                    value={floatAmount}
                    onChange={(e) => setFloatAmount(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg tabular-nums text-lg font-semibold focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Why the float is changing"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mahlomola's PIN</label>
                <input
                  type="password"
                  value={managerPin}
                  onChange={(e) => setManagerPin(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg tracking-widest focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Only Mahlomola can authorise a float change.</p>
              </div>
            </div>
          )}

          <button
            onClick={openShift}
            disabled={busy}
            className="w-full mt-5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg"
          >
            {busy ? 'Opening…' : 'Open shift'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* What the system expects */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900">What the system expects</h3>
            <p className="text-sm text-gray-500 mb-4">
              {shift.saleCount ?? 0} sales since the shift opened
            </p>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Opening float</td>
                  <td className="py-2 text-right tabular-nums">R {Number(shift.floatOpening ?? 0).toFixed(2)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Cash sales</td>
                  <td className="py-2 text-right tabular-nums">R {Number(shift.cashSales ?? 0).toFixed(2)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Petty cash out</td>
                  <td className="py-2 text-right tabular-nums text-red-600">
                    − R {Number(shift.pettyCash ?? 0).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 font-bold text-gray-900">Expected in drawer</td>
                  <td className="py-3 text-right tabular-nums font-bold text-xl">R {expected.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-5 mb-2">
              Not cash — no count needed
            </h4>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Card</td>
                  <td className="py-2 text-right tabular-nums">R {Number(shift.cardSales ?? 0).toFixed(2)}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 text-gray-600">Mobile</td>
                  <td className="py-2 text-right tabular-nums">R {Number(shift.mobileSales ?? 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-600">On account</td>
                  <td className="py-2 text-right tabular-nums">R {Number(shift.accountSales ?? 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* What was counted */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900">What you counted</h3>
            <p className="text-sm text-gray-500 mb-4">Count the drawer and enter the total.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Counted cash</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-medium">R</span>
              <input
                type="number"
                value={counted}
                onChange={(e) => setCounted(e.target.value)}
                placeholder="0.00"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg tabular-nums text-xl font-bold focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {variance !== null && (
              <div
                className={`mt-4 rounded-lg p-4 border ${
                  Math.abs(variance) < 0.005
                    ? 'bg-green-50 border-green-200'
                    : overTolerance
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex justify-between items-baseline">
                  <span
                    className={`font-bold text-sm ${
                      Math.abs(variance) < 0.005 ? 'text-green-800' : overTolerance ? 'text-red-700' : 'text-amber-800'
                    }`}
                  >
                    {Math.abs(variance) < 0.005 ? 'Balanced' : variance < 0 ? 'Short by' : 'Over by'}
                  </span>
                  <span
                    className={`tabular-nums font-bold text-xl ${
                      Math.abs(variance) < 0.005 ? 'text-green-800' : overTolerance ? 'text-red-700' : 'text-amber-800'
                    }`}
                  >
                    R {Math.abs(variance).toFixed(2)}
                  </span>
                </div>
                {overTolerance && (
                  <p className="text-xs mt-2 text-red-700">
                    Over the R{TOLERANCE} tolerance — a reason is required, and this is recorded against the shift.
                  </p>
                )}
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
              Notes {overTolerance && <span className="text-red-600">(required)</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything that explains the difference"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />

            <button
              onClick={closeShift}
              disabled={busy}
              className="w-full mt-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg"
            >
              {busy ? 'Closing…' : 'Close shift'}
            </button>
          </div>
        </div>
      )}

      {shift && (
        <PettyCashPanel branchId={branchId} shiftId={shift.shiftId} onChange={load} />
      )}

      {history.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 px-6 py-4 border-b">Recent shifts</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Closed</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Expected</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Counted</th>
                  <th className="text-right py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Variance</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h: any, i: number) => {
                  const v = Number(h.variance ?? 0);
                  return (
                    <tr key={h.id || i} className="border-b last:border-0">
                      <td className="py-2.5 px-6 text-gray-700">
                        {h.closedAt ? new Date(h.closedAt).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      </td>
                      <td className="py-2.5 px-4 text-right tabular-nums">R {Number(h.expectedCash ?? 0).toFixed(2)}</td>
                      <td className="py-2.5 px-4 text-right tabular-nums">R {Number(h.countedCash ?? 0).toFixed(2)}</td>
                      <td
                        className={`py-2.5 px-6 text-right tabular-nums font-semibold ${
                          Math.abs(v) < 0.005 ? 'text-green-700' : 'text-red-600'
                        }`}
                      >
                        {v > 0 ? '+' : ''}
                        R {v.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
